import { Navigate, Outlet } from 'react-router-dom';

import { HOME_ROUTE } from '../constants';
import { useAuth } from '../auth-context';
import type { Permission } from '../permissions';
import { useCan } from '../use-can';
import { AuthSplash } from './auth-splash';

/**
 * Permission guard over `ProtectedRoute` (SR-228): a Team Portal section is drawn only for an
 * account holding the permission that opens it — survey-api's policy for the section's reads,
 * named once per route (the role-model UI design's §4 table).
 *
 * It reads `user.permissions` — what `GET /auth/me` returns — and nothing of the login payload,
 * even though both come from the server. The stored session is the copy an attacker can edit in
 * localStorage; `user` is re-fetched on every mount, on a 403 and when the Team Portal's tab is
 * shown, so it cannot be talked into a permission from the browser.
 *
 * **This is not the access control.** It decides what to render, nothing more: a check in a
 * bundle the client owns can only hide a screen. What is behind it is survey-api's to protect,
 * and survey-api does — every route answers 403 to an account without its policy's permission
 * before any lookup.
 *
 * The `permission` default is `roles.manage` on purpose: a route that forgets to name its
 * permission gets the narrowest audience, the administrators', not a wider one.
 *
 * The Advisor Portal needs no guard of this kind: its zone's element is its guard, and turns a
 * Team Portal account away at every address under it — "Add a survey" included, which survey-api
 * would bind to a staff session as readily and the product does not offer to one.
 *
 * An advisor never reaches it — the Team Portal's zone draws its boundary page first — so the
 * one caller it refuses is a Team Portal account without the permission, sent to `/`, which lands
 * it on the Team Portal's Home: a screen of its own portal, not a refusal of a portal that is
 * its. A permission taken away under an open section refuses the same way, on the re-render the
 * next `/auth/me` causes.
 */
export function AdminRoute({ permission = 'roles.manage' }: { permission?: Permission }) {
  const { status, user } = useAuth();
  const can = useCan();

  if (status === 'pending') {
    return <AuthSplash />;
  }

  if (!user || !can(permission)) {
    return <Navigate to={HOME_ROUTE} replace />;
  }

  return <Outlet />;
}
